import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { FusionStores } from '@fusion/saas-components-v3'
import {
  streamLatestTelemetry,
  streamCurrentReadings,
  streamDeviceState,
  type StreamReturnObject
} from '@/services/device-metadata'
import { FeatureFlag } from '@/services/feature-flags'
import type { RootState } from '@/store'
import { NAMESPACES } from '@/store'
import { DEVICE_ACTIONS } from '@/store/devices'

export function useStreaming() {
  const store = useStore<RootState>()
  const featureFlagsStore = FusionStores.featureFlags()

  const loadDataInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const telemetryStream = ref<StreamReturnObject | null>(null)
  const currentReadingsStream = ref<StreamReturnObject | null>(null)
  const deviceStateStream = ref<StreamReturnObject | null>(null)

  const enableTelemetryStreaming = computed<boolean>(() =>
    featureFlagsStore.featureFlagBySlug(FeatureFlag.StreamLatestTelemetry)
  )

  const updateLatestTelemetryFromStream = (deviceId: string) =>
    store.dispatch(
      `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_LATEST_TELEMETRY_FROM_STREAM}`,
      deviceId
    )
  const updateCurrentReadings = (data: unknown) =>
    store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_CURRENT_READINGS}`, data)
  const updateDeviceState = (data: unknown) =>
    store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_DEVICE_STATE}`, data)

  // opens a telemetry stream for a device that will update the stores latest telemetry
  // accepts an optional callback function to execute additional logic on each update
  async function openTelemetryStream(
    deviceId: string,
    callback: () => void = () => {}
  ): Promise<void> {
    closeTelemetryStream()
    telemetryStream.value = await streamLatestTelemetry(deviceId, () => {
      updateLatestTelemetryFromStream(deviceId)
      callback()
    })
  }

  function closeTelemetryStream(): void {
    if (telemetryStream.value?.stream?.cancel) {
      telemetryStream.value.stream.cancel()
    }
    telemetryStream.value = null
  }

  async function openCurrentReadingsStream(deviceId: string): Promise<void> {
    closeCurrentReadingsStream()
    currentReadingsStream.value = await streamCurrentReadings(deviceId, updateCurrentReadings)
  }

  function closeCurrentReadingsStream(): void {
    if (currentReadingsStream.value?.stream?.cancel) {
      currentReadingsStream.value.stream.cancel()
    }
    currentReadingsStream.value = null
  }

  async function openDeviceStateStream(deviceId: string): Promise<void> {
    closeDeviceStateStream()
    deviceStateStream.value = await streamDeviceState(deviceId, updateDeviceState)
  }

  function closeDeviceStateStream(): void {
    if (deviceStateStream.value?.stream?.cancel) {
      deviceStateStream.value.stream.cancel()
    }
    deviceStateStream.value = null
  }

  return {
    loadDataInterval,
    telemetryStream,
    currentReadingsStream,
    deviceStateStream,
    enableTelemetryStreaming,
    openTelemetryStream,
    closeTelemetryStream,
    openCurrentReadingsStream,
    closeCurrentReadingsStream,
    openDeviceStateStream,
    closeDeviceStateStream
  }
}
